import React, { useCallback } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  Tab,
  TabTitleText,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { observer, useLocalStore } from 'mobx-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-mobx-router5';

import useMobxStores from 'hooks/useMobxStores';
import ErrorState from 'components/Error/ErrorState';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import Objectives from 'components/Course/OverviewTab/Objectives';
import PremiumSessionsList from 'components/PremiumSessions';
import useTabs from 'hooks/useTabs';
import usePremiumSessionsHandlers from 'components/Landing/hooks/usePremiumSessionsHandlers';
import { LandingLayout } from 'components/Landing/LandingLayout';
import LandingPageSkeleton from 'components/Landing/LandingPageSkeleton';
import PremiumLandingStore from './store';
import SidebarButtons from './SidebarButtons';
import { PremiumLandingTab } from './types';
import usePremiumLandingData from './hooks/usePremiumLandingData';
import usePremiumLandingRedirect from './hooks/usePremiumLandingRedirect';

/**
 * Premium Landing page for premium session offerings.
 *
 * Cards always navigate here first. Once the catalog is loaded, we check if this
 * premium session has a linked course in the catalog. If so, we redirect (replace)
 * to the Course Landing page at the premium_sessions tab for a richer experience.
 * If the session is independent (no catalog entry), we stay and show the premium view.
 */
const PremiumLanding = () => {
  const { t } = useTranslation();

  const { catalogStore, eventStore, routerStore, uiStore, userStore } =
    useMobxStores();

  const { route } = routerStore;
  const { session: sessionSlug } = (route as any).params;

  const redirectToCourseLanding = usePremiumLandingRedirect({
    sessionSlug,
    catalogStore,
    routerStore,
  });

  const {
    fetchOverviewData,
    title,
    isLoading,
    isDataLoaded,
    isError,
    externalLoadingError,
    coursePremiumSessionCollateral,
  } = useLocalStore(() => new PremiumLandingStore(uiStore, userStore));

  const {
    handleBulkScheduleButton,
    handleCancelBulkScheduling,
    handleConfirmEnrollment,
    handleSelect,
    hasPremiumSessions,
    isEnrolling,
    isModalOpen,
    liveSessionListRef,
    premiumSessionAggregate,
    selectedPremiumSessions,
    selectionMode,
    sessionCount,
    setIsModalOpen,
    slugsWithPremiumSessions,
  } = usePremiumSessionsHandlers(sessionSlug);

  usePremiumLandingData({
    sessionSlug,
    redirectToCourseLanding,
    fetchOverviewData,
    externalLoadingError,
  });

  const handleNavigate = useCallback(
    (tab: PremiumLandingTab) => {
      const params: Record<string, string> = { session: sessionSlug };
      if (tab !== PremiumLandingTab.Overview) params.tab = tab;
      routerStore.navigate('premium:sessions:overview', params);
    },
    [sessionSlug, routerStore],
  );

  const { activeTabKey, handleTabClick } = useTabs<PremiumLandingTab>({
    route: route as { params?: Record<string, string> },
    defaultTab: PremiumLandingTab.Overview,
    isValidTab: (v): v is PremiumLandingTab =>
      Object.values(PremiumLandingTab).includes(v as PremiumLandingTab),
    onNavigate: handleNavigate,
  });

  if (redirectToCourseLanding) {
    return null;
  }

  if (!catalogStore.hasCatalogLoaded) {
    return (
      <LandingLayout>
        <LandingPageSkeleton
          breadcrumbParentLink={
            <Link href routeName="premium:sessions:live">
              {t('Sessions')}
            </Link>
          }
          sidebarShowMetadata={false}
        />
      </LandingLayout>
    );
  }

  if (isError) {
    return (
      <LandingLayout>
        <LandingLayout.Breadcrumb>
          <Breadcrumb>
            <BreadcrumbItem>
              <Link href routeName="premium:sessions:live">
                {t('Sessions')}
              </Link>
            </BreadcrumbItem>
            <BreadcrumbItem isActive>{t('Error')}</BreadcrumbItem>
          </Breadcrumb>
        </LandingLayout.Breadcrumb>
        <ErrorState
          alwaysShowSupport
          showExploreSubscriptions={false}
          title={t('Could not load the session')}
          onRetry={() => {
            if (sessionSlug) {
              fetchOverviewData(sessionSlug);
            }
          }}
          onSupportClick={() => {
            eventStore.toggleDrawer('support_form');
          }}
        />
      </LandingLayout>
    );
  }

  return (
    <LandingLayout>
      {isLoading ? (
        <LandingPageSkeleton
          breadcrumbParentLink={
            <Link href routeName="premium:sessions:live">
              {t('Sessions')}
            </Link>
          }
          sidebarShowMetadata={false}
        />
      ) : (
        isDataLoaded && (
          <>
            <Helmet title={`${title} | Overview - Red Hat Learning`} />
            <LandingLayout.Breadcrumb>
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link href routeName="premium:sessions:live">
                    {t('Sessions')}
                  </Link>
                </BreadcrumbItem>
                <BreadcrumbItem isActive>{title}</BreadcrumbItem>
              </Breadcrumb>
            </LandingLayout.Breadcrumb>
            <LandingLayout.Header title={title} />
            <LandingLayout.Content>
              <LandingLayout.Main>
                <LandingLayout.Tabs
                  mountOnEnter
                  isBox
                  activeKey={activeTabKey}
                  onSelect={handleTabClick}
                >
                  <Tab
                    eventKey={PremiumLandingTab.Overview}
                    title={<TabTitleText>{t('Overview')}</TabTitleText>}
                    aria-label={t(
                      'Session overview containing detailed information',
                    )}
                    data-analytics-id="overview-tab-premium-landing-view-lp"
                  >
                    {coursePremiumSessionCollateral?.objectives?.length ? (
                      <Objectives
                        objectives={coursePremiumSessionCollateral.objectives}
                      />
                    ) : (
                      <LandingLayout.EmptyMessage>
                        {t('No overview content available')}
                      </LandingLayout.EmptyMessage>
                    )}
                  </Tab>

                  <Tab
                    eventKey={PremiumLandingTab.PremiumSessions}
                    title={
                      <TabTitleText>
                        {t('Live sessions ({{sessionCount}})', {
                          sessionCount,
                        })}
                      </TabTitleText>
                    }
                    aria-label={t('View the live sessions for this offering')}
                    data-analytics-id="livesessions-tab-premium-landing-view-lp"
                  >
                    <div ref={liveSessionListRef} />
                    <PremiumSessionsList
                      coursePremiumSessionCollateral={
                        coursePremiumSessionCollateral
                      }
                      courseTitle={title}
                      showObjectivesFromCollateral={false}
                      isModalOpen={isModalOpen}
                      onCardSelect={handleSelect}
                      onConfirmEnrollment={handleConfirmEnrollment}
                      premiumSessionAggregate={premiumSessionAggregate}
                      selectedPremiumSessions={selectedPremiumSessions}
                      selectionMode={selectionMode}
                      setIsModalOpen={setIsModalOpen}
                      slugsWithPremiumSessions={slugsWithPremiumSessions}
                    />
                  </Tab>
                </LandingLayout.Tabs>
              </LandingLayout.Main>

              <LandingLayout.Sidebar>
                <SidebarButtons
                  activeTab={activeTabKey}
                  hasPremiumSessions={hasPremiumSessions}
                  isEnrolling={isEnrolling}
                  onBulkScheduleButton={handleBulkScheduleButton}
                  onCancelBulkScheduling={handleCancelBulkScheduling}
                  selectedPremiumSessions={selectedPremiumSessions}
                  selectionMode={selectionMode}
                  setIsModalOpen={setIsModalOpen}
                  totalSessions={sessionCount}
                />

                <ExploreSubscriptions />
              </LandingLayout.Sidebar>
            </LandingLayout.Content>
          </>
        )
      )}
    </LandingLayout>
  );
};

export default observer(PremiumLanding);
